/**
 * The members each slot of a document declares.
 *
 * A document is checked in three places before it becomes a form — the TypeScript type when it is
 * written as a literal, the published JSON Schema when an editor reads it, and this parser — and the
 * first two are the two a document can arrive without. A form config stored in a CMS, generated by a
 * model or assembled by a server was never a literal and never met an editor; the parser is all it
 * meets, and a member nobody declared went through it without a word.
 *
 * The lists are here rather than beside each check because they are also compared: the contract
 * audit holds them against the `properties` of the published schemas, so a member added to one and
 * not the other is a gate failure rather than a document that one surface accepts and another
 * refuses.
 *
 * Reporting is not refusing. A reader may meet a document carrying members it predates — that is
 * what lets a v3 document be read by a v2 parser — so an unknown member is a finding, and what a
 * publishing gate does with a finding is the gate's decision.
 */
/** Members every field carries, whatever its kind. */
const FIELD_BASE = [
    "name",
    "kind",
    "label",
    "ariaLabel",
    "placeholder",
    "supportingText",
    "initialValue",
    "validators",
    "sensitive",
    "prefix",
    "suffix",
];
/** Members a kind adds. A document naming one on the wrong kind is a document naming it. */
const FIELD_BY_KIND = [
    // number, slider
    "min",
    "max",
    "step",
    // select, radio, multiselect, segmented
    "options",
    "mode",
    "searchable",
    "reorderable",
    "loading",
    // datepicker, timepicker, daterange
    "locale",
    "firstDayOfWeek",
    "minDate",
    "maxDate",
    "format",
    "viewMode",
    "granularity",
    "animateHand",
    "showUnavailable",
    // file
    "accept",
    "multiple",
    // colors
    "presets",
];
/**
 * Every member of every slot, by the name the audit and the parser both use.
 *
 * A tree field node carries the field under `field` and takes its name from the key the parent
 * stores it under; `name` stays in the field's own list because the flat form is where a field
 * carries it, and a tree document that repeats it is naming a member the contract has.
 */
/**
 * The version each member arrived with, for the members that did not arrive with v2.
 *
 * Listed once and read twice: the parser refuses a member in a document whose version predates it,
 * and the schema audit excuses the published schemas that were written before it existed. The
 * alternative — the arrival spelled out in each reader — is a pair that agrees until somebody edits
 * one of them.
 *
 * A member absent from this table arrived with v2, which is where the contract starts.
 */
export const MDY_DYNAMIC_MEMBER_ARRIVALS = Object.freeze({
    document: Object.freeze({ requiresContext: 4 }),
    validators: Object.freeze({ integer: 5 }),
});
export const MDY_DYNAMIC_MEMBERS = Object.freeze({
    /**
     * The envelope's own members, every version's together.
     *
     * A member a version predates is reported as a version finding rather than an unknown one — the
     * document is written against a contract, and being told which contract has the member is the more
     * useful sentence.
     */
    document: Object.freeze([
        "version", "id", "fields", "schema", "layout", "rules", "validations", "requiresContext",
    ]),
    field: Object.freeze([...FIELD_BASE, ...FIELD_BY_KIND]),
    validators: Object.freeze([
        "required", "email", "integer", "min", "max", "minLength", "maxLength", "pattern", "messages",
    ]),
    option: Object.freeze(["value", "label", "disabled"]),
    rule: Object.freeze(["effect", "target", "when"]),
    validation: Object.freeze(["when", "message", "target"]),
    layoutSection: Object.freeze(["kind", "id", "label", "at", "children"]),
    layoutColumns: Object.freeze(["kind", "id", "at", "columns"]),
    layoutSlot: Object.freeze(["ref", "at"]),
});
/**
 * The members of `value` that its slot does not declare, in the order the document wrote them.
 *
 * Anything that is not an object has no members to report: it is refused elsewhere, for being the
 * wrong shape, and a second finding about the same thing sends a reader to two places for one fault.
 */
export function unknownMembers(value, declared) {
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return [];
    return Object.keys(value).filter((member) => !declared.includes(member));
}
